import { resolveLocale } from '@powerful/config/i18n';
import {
  DiscoveryDataError,
  suggestSearch,
  SUGGEST_MIN_CHARS,
  type DiscoveryErrorCode,
  type SearchSuggestion,
} from '@powerful/data';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { getSupabaseClient } from '../lib/supabase';

export const SUGGEST_DEBOUNCE_MS = 200;
const SUGGEST_CACHE_MS = 5 * 60_000;
const EMPTY: SearchSuggestion[] = [];

export interface SearchSuggestionsState {
  suggestions: SearchSuggestion[];
  /** `idle` = nada a pedir (texto curto ou offline); o campo continua utilizável em qualquer estado. */
  status: 'idle' | 'loading' | 'success' | 'error';
  errorCode: DiscoveryErrorCode | null;
  isOffline: boolean;
}

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

const readOnline = () => navigator.onLine;

/** Chave de cache: "Cab", "cab " e "cab" são a mesma consulta. */
function normalizeQuery(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLowerCase();
}

function usefulLength(q: string): number {
  return q
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '').length;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Sugestões do campo de busca (T2.8, PRD §5.1.1).
 *
 * Espera 200 ms de pausa na digitação, cancela a requisição anterior (o `signal` do React Query
 * aborta a query que deixou de ser observada), guarda cada `(consulta, locale)` por 5 min e não
 * dispara offline. Sem retry: uma sugestão atrasada já não serve — a próxima tecla pede outra.
 * O locale vem do i18n ativo, espelho de `users.locale`. Nenhum histórico é guardado (J15).
 */
export function useSearchSuggestions(text: string): SearchSuggestionsState {
  const { i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const online = useSyncExternalStore(subscribeOnline, readOnline, () => true);
  const q = useDebouncedValue(normalizeQuery(text), SUGGEST_DEBOUNCE_MS);
  const enabled = online && usefulLength(q) >= SUGGEST_MIN_CHARS;

  const query = useQuery({
    queryKey: ['search-suggestions', q, locale],
    queryFn: ({ signal }) => suggestSearch(getSupabaseClient(), { q, locale }, signal),
    enabled,
    staleTime: SUGGEST_CACHE_MS,
    gcTime: SUGGEST_CACHE_MS,
    retry: false,
    refetchOnReconnect: false,
  });

  if (!enabled) {
    return { suggestions: EMPTY, status: 'idle', errorCode: null, isOffline: !online };
  }
  if (query.isError) {
    const errorCode = query.error instanceof DiscoveryDataError ? query.error.code : 'unknown';
    return { suggestions: EMPTY, status: 'error', errorCode, isOffline: false };
  }
  if (query.isSuccess) {
    return { suggestions: query.data, status: 'success', errorCode: null, isOffline: false };
  }
  return { suggestions: EMPTY, status: 'loading', errorCode: null, isOffline: false };
}
